/**
 * ReprintRemakeModal - Unified single-screen modal for reporting reprints/remakes
 *
 * Consolidates all rework reporting entry points:
 * - Header "Reprint/Remake" button
 * - PA Queue "Report Issue" button
 * - Assembly Queue "Report Issue" button
 */

import { useState, useEffect, useRef } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || '';

// Rework types for "What's the Rework?"
const REWORK_TYPES = [
  { code: 'reprint', label: 'Reprint', description: '+ additional relevant tasks' },
  { code: 'opti_assembly_only', label: 'Opti/Assembly Only', description: 'No reprints needed' },
  { code: 'file_prep_color_dev', label: 'File Prep / Color Dev', description: 'No reprints needed' },
  { code: 'repaint', label: 'Repaint' },
  { code: 'other', label: 'Other' },
];

// Reasons for "What's the Reason?"
const REASON_TYPES = [
  { code: 'operator_error', label: 'Operator Error' },
  { code: 'equipment_issue', label: 'Equipment Issue' },
  { code: 'material_issue', label: 'Material Issue' },
  { code: 'file_issue', label: 'File/Art Issue' },
  { code: 'customer_change', label: 'Customer Change' },
  { code: 'other', label: 'Other' },
];

export default function ReprintRemakeModal({
  isOpen,
  onClose,
  preselectedQuote = null,
  preselectedJobs = null,
  workCenter = null,
  currentUser = null,
  token = null,
}) {
  // Quote/Project state
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [quoteSearch, setQuoteSearch] = useState('');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteDropdown, setShowQuoteDropdown] = useState(false);
  const [quotesLoading, setQuotesLoading] = useState(false);

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState({}); // { jobId: { selected: bool, qty: number, job: object } }
  const [jobsLoading, setJobsLoading] = useState(false);

  // Form state
  const [selectedRework, setSelectedRework] = useState(null);
  const [selectedReason, setSelectedReason] = useState(null);
  const [notes, setNotes] = useState('');

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const quoteInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Load quotes on mount
  useEffect(() => {
    if (!isOpen) return;
    loadQuotes();
  }, [isOpen, token]);

  // Handle preselected quote
  useEffect(() => {
    if (preselectedQuote && quotes.length > 0) {
      const quote = quotes.find(q =>
        q.quoteNumber?.toString() === preselectedQuote?.toString()
      );
      if (quote) {
        setSelectedQuote(quote);
        setQuoteSearch(`${quote.quoteNumber} - ${quote.user_Text4 || quote.customerName || ''}`);
      }
    }
  }, [preselectedQuote, quotes]);

  // Load jobs when quote is selected
  useEffect(() => {
    if (selectedQuote) {
      loadJobs(selectedQuote.quoteNumber);
    } else {
      setJobs([]);
      setSelectedJobs({});
    }
  }, [selectedQuote]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowQuoteDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter quotes based on search
  useEffect(() => {
    if (!quoteSearch.trim()) {
      setFilteredQuotes(quotes.slice(0, 50)); // Show first 50 by default
      return;
    }
    const term = quoteSearch.toLowerCase();
    const filtered = quotes.filter(q =>
      q.quoteNumber?.toString().includes(term) ||
      (q.user_Text4 || '').toLowerCase().includes(term) ||
      (q.customerName || '').toLowerCase().includes(term)
    );
    setFilteredQuotes(filtered.slice(0, 50));
  }, [quoteSearch, quotes]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedQuote(null);
      setQuoteSearch('');
      setJobs([]);
      setSelectedJobs({});
      setSelectedRework(null);
      setSelectedReason(null);
      setNotes('');
      setError(null);
    }
  }, [isOpen]);

  const loadQuotes = async () => {
    setQuotesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/quotes`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to load quotes');
      const data = await res.json();
      setQuotes(data.quotes || []);
      setFilteredQuotes((data.quotes || []).slice(0, 50));
    } catch (err) {
      console.error('Error loading quotes:', err);
      setError('Failed to load quotes');
    } finally {
      setQuotesLoading(false);
    }
  };

  const loadJobs = async (quoteNumber) => {
    setJobsLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // First, get orders for this quote
      const ordersRes = await fetch(`${API_BASE}/api/orders?quoteNumber=${encodeURIComponent(quoteNumber)}`, { headers });
      if (!ordersRes.ok) throw new Error('Failed to load orders');
      const ordersData = await ordersRes.json();
      const orders = ordersData.orders || [];

      if (orders.length === 0) {
        // No orders, try direct jobs endpoint as fallback
        const jobsRes = await fetch(`${API_BASE}/api/jobboss/quotes/${quoteNumber}/jobs`, { headers });
        if (jobsRes.ok) {
          const jobsData = await jobsRes.json();
          const jobList = jobsData.jobs || [];
          setJobs(jobList);
          initializeJobSelection(jobList);
        } else {
          setJobs([]);
        }
        return;
      }

      // Get jobs for all orders
      const allJobs = [];
      for (const order of orders) {
        try {
          const jobsRes = await fetch(`${API_BASE}/api/orders/${order.orderNumber}/jobs`, { headers });
          if (jobsRes.ok) {
            const jobsData = await jobsRes.json();
            const orderJobs = (jobsData.jobs || []).map(job => ({
              ...job,
              orderNumber: order.orderNumber,
            }));
            allJobs.push(...orderJobs);
          }
        } catch (e) {
          console.error(`Error loading jobs for order ${order.orderNumber}:`, e);
        }
      }

      setJobs(allJobs);
      initializeJobSelection(allJobs);
    } catch (err) {
      console.error('Error loading jobs:', err);
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  };

  const initializeJobSelection = (jobList) => {
    const initialSelection = {};
    jobList.forEach(job => {
      const jobId = job.jobNumber || job.job || job.id;
      initialSelection[jobId] = {
        selected: preselectedJobs?.includes(jobId) || false,
        qty: job.orderQty || job.quantity || job.qty || 1,
        job,
      };
    });
    setSelectedJobs(initialSelection);
  };

  const handleQuoteSelect = (quote) => {
    setSelectedQuote(quote);
    setQuoteSearch(`${quote.quoteNumber} - ${quote.user_Text4 || quote.customerName || ''}`);
    setShowQuoteDropdown(false);
  };

  const handleJobToggle = (jobId) => {
    setSelectedJobs(prev => ({
      ...prev,
      [jobId]: {
        ...prev[jobId],
        selected: !prev[jobId]?.selected,
      },
    }));
  };

  const handleQtyChange = (jobId, qty) => {
    const numQty = parseInt(qty, 10) || 1;
    setSelectedJobs(prev => ({
      ...prev,
      [jobId]: {
        ...prev[jobId],
        qty: Math.max(1, numQty),
      },
    }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(selectedJobs).every(j => j.selected);
    setSelectedJobs(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(jobId => {
        updated[jobId] = { ...updated[jobId], selected: !allSelected };
      });
      return updated;
    });
  };

  const handleSubmit = async () => {
    // Validation
    const selected = Object.entries(selectedJobs).filter(([_, v]) => v.selected);
    if (selected.length === 0) {
      setError('Please select at least one job/SKU');
      return;
    }
    if (!selectedRework) {
      setError('Please select what the rework is');
      return;
    }
    if (!selectedReason) {
      setError('Please select the reason');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Submit one incident per selected job
      const promises = selected.map(([jobId, { qty, job }]) =>
        fetch(`${API_BASE}/api/rework-events`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            quoteNumber: selectedQuote?.quoteNumber,
            orderNumber: job?.orderNumber || selectedQuote?.orders?.[0]?.orderNumber,
            jobNumber: jobId,
            projectName: selectedQuote?.user_Text4 || selectedQuote?.customerName,
            discoveredWorkCenter: workCenter || 'UNKNOWN',
            issueType: selectedRework,
            reason: selectedReason,
            quantity: qty,
            description: notes || undefined,
            reportedById: currentUser?.employeeId,
            reportedByName: currentUser?.displayName,
          }),
        })
      );

      await Promise.all(promises);
      onClose();
    } catch (err) {
      console.error('Error submitting rework:', err);
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedCount = Object.values(selectedJobs).filter(j => j.selected).length;
  const canSubmit = selectedCount > 0 && selectedRework && selectedReason && !submitting;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-amber-50 to-yellow-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Report Reprint/Remake</h2>
            <p className="text-sm text-gray-500">Report an issue that requires rework</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Error display */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* 1. Quote/Project Selector */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Quote / Project
            </label>
            <div className="relative" ref={dropdownRef}>
              <input
                ref={quoteInputRef}
                type="text"
                value={quoteSearch}
                onChange={(e) => {
                  setQuoteSearch(e.target.value);
                  setShowQuoteDropdown(true);
                  if (!e.target.value) setSelectedQuote(null);
                }}
                onFocus={() => setShowQuoteDropdown(true)}
                placeholder="Search by quote number or project name..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-900"
              />
              {quotesLoading && (
                <div className="absolute right-3 top-3.5">
                  <svg className="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              )}

              {/* Dropdown */}
              {showQuoteDropdown && filteredQuotes.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredQuotes.map((quote) => (
                    <button
                      key={quote.quoteNumber}
                      onClick={() => handleQuoteSelect(quote)}
                      className="w-full px-4 py-3 text-left hover:bg-amber-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">
                        {quote.quoteNumber} - {quote.user_Text4 || 'Untitled'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {quote.customerName || 'Unknown Customer'}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 2. Job/SKU Selector with Quantity */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-gray-700">
                Jobs / SKUs
              </label>
              {jobs.length > 0 && (
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                >
                  {Object.values(selectedJobs).every(j => j.selected) ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>

            {!selectedQuote ? (
              <div className="p-4 bg-gray-50 rounded-lg text-gray-500 text-sm text-center">
                Select a quote to see available jobs
              </div>
            ) : jobsLoading ? (
              <div className="p-4 bg-gray-50 rounded-lg text-gray-500 text-sm text-center">
                Loading jobs...
              </div>
            ) : jobs.length === 0 ? (
              <div className="p-4 bg-gray-50 rounded-lg text-gray-500 text-sm text-center">
                No jobs found for this quote
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                {jobs.map((job) => {
                  const jobId = job.jobNumber || job.job;
                  const jobData = selectedJobs[jobId] || { selected: false, qty: job.orderQty || job.quantity || 1 };
                  const isSelected = jobData.selected;
                  const totalQty = job.orderQty || job.quantity || job.qty || 0;
                  return (
                    <div
                      key={jobId}
                      className={`p-3 flex items-center gap-3 ${isSelected ? 'bg-amber-50' : 'hover:bg-gray-50'}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleJobToggle(jobId)}
                        className="h-4 w-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900">{jobId}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {job.partNumber && <span>{job.partNumber} - </span>}
                          {job.description || job.partDesc || job.name || 'No description'}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-600">Qty:</label>
                          <input
                            type="number"
                            min="1"
                            max={totalQty || 999}
                            value={jobData.qty}
                            onChange={(e) => handleQtyChange(jobId, e.target.value)}
                            className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-amber-500"
                          />
                          {totalQty > 0 && <span className="text-xs text-gray-400">/ {totalQty}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {selectedCount > 0 && (
              <div className="mt-2 text-sm text-amber-600 font-medium">
                {selectedCount} job{selectedCount !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>

          {/* 3. What's the Rework? */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              What's the Rework?
            </label>
            <div className="grid grid-cols-2 gap-2">
              {REWORK_TYPES.map((rework) => (
                <button
                  key={rework.code}
                  onClick={() => setSelectedRework(rework.code)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium border transition-all text-left ${
                    selectedRework === rework.code
                      ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                  }`}
                >
                  <div>{rework.label}</div>
                  {rework.description && (
                    <div className={`text-xs mt-0.5 ${selectedRework === rework.code ? 'text-amber-100' : 'text-gray-400'}`}>
                      {rework.description}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* 4. What's the Reason? */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              What's the Reason?
            </label>
            <div className="grid grid-cols-3 gap-2">
              {REASON_TYPES.map((reason) => (
                <button
                  key={reason.code}
                  onClick={() => setSelectedReason(reason.code)}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                    selectedReason === reason.code
                      ? 'bg-blue-500 text-white border-blue-500 shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  {reason.label}
                </button>
              ))}
            </div>
          </div>

          {/* 5. Notes (Optional) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Notes <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details about the issue..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-900 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`px-6 py-2.5 rounded-lg font-semibold transition-all ${
              canSubmit
                ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-sm hover:shadow'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
}
